const beginnerTest = {
  id: 'beginner',
  name: 'Beginner SQL',
  subtitle: 'Learn the basics',
  description: 'Practice fundamental SQL queries with a simple products and orders database',

  schema: `
    CREATE TABLE products (
      id INTEGER PRIMARY KEY,
      name TEXT NOT NULL,
      category TEXT NOT NULL,
      price REAL NOT NULL
    );

    CREATE TABLE orders (
      id INTEGER PRIMARY KEY,
      product_id INTEGER NOT NULL,
      quantity INTEGER NOT NULL,
      order_date TEXT NOT NULL,
      FOREIGN KEY (product_id) REFERENCES products(id)
    );
  `,

  seedDataFn: (db) => {
    // Insert products
    const products = [
      [1, 'Laptop', 'Electronics', 899.99],
      [2, 'Mouse', 'Electronics', 24.99],
      [3, 'Keyboard', 'Electronics', 79.99],
      [4, 'Monitor', 'Electronics', 199.99],
      [5, 'Desk Chair', 'Furniture', 149.99],
      [6, 'Standing Desk', 'Furniture', 399.99],
      [7, 'Notebook', 'Stationery', 4.99],
      [8, 'Pen Set', 'Stationery', 12.99],
      [9, 'Water Bottle', 'Accessories', 19.99],
      [10, 'Coffee Mug', 'Accessories', 9.99]
    ];

    const productStmt = db.prepare(
      'INSERT INTO products (id, name, category, price) VALUES (?, ?, ?, ?)'
    );
    products.forEach(p => productStmt.run(p));
    productStmt.free();

    // Insert orders
    const orders = [
      [1, 1, 2, '2026-01-15'],
      [2, 2, 5, '2026-01-16'],
      [3, 3, 3, '2026-01-17'],
      [4, 7, 10, '2026-01-18'],
      [5, 8, 4, '2026-01-19'],
      [6, 1, 1, '2026-01-20'],
      [7, 5, 2, '2026-01-21'],
      [8, 6, 1, '2026-01-22'],
      [9, 9, 3, '2026-01-23'],
      [10, 10, 6, '2026-01-24'],
      [11, 4, 2, '2026-02-01'],
      [12, 2, 8, '2026-02-02'],
      [13, 3, 2, '2026-02-03'],
      [14, 7, 15, '2026-02-04'],
      [15, 1, 1, '2026-02-05'],
      [16, 5, 1, '2026-02-06'],
      [17, 8, 3, '2026-02-07'],
      [18, 9, 2, '2026-02-08'],
      [19, 10, 4, '2026-02-09'],
      [20, 4, 1, '2026-02-10']
    ];

    const orderStmt = db.prepare(
      'INSERT INTO orders (id, product_id, quantity, order_date) VALUES (?, ?, ?, ?)'
    );
    orders.forEach(o => orderStmt.run(o));
    orderStmt.free();
  },

  questions: [
    {
      id: 1,
      difficulty: 'Easy',
      question: 'Select all products from the database.',
      hint: 'Use SELECT * to get all columns from a table.',
      timeLimit: 120, // 2 minutes
      referenceSQL: 'SELECT * FROM products ORDER BY id',
      solutionSQL: 'SELECT * FROM products ORDER BY id',
      expectedColumns: ['id', 'name', 'category', 'price']
    },
    {
      id: 2,
      difficulty: 'Easy',
      question: 'Find all products with a price under £50.',
      hint: 'Use the WHERE clause to filter rows based on a condition.',
      timeLimit: 180, // 3 minutes
      referenceSQL: 'SELECT * FROM products WHERE price < 50 ORDER BY price',
      solutionSQL: 'SELECT * FROM products WHERE price < 50 ORDER BY price',
      expectedColumns: ['id', 'name', 'category', 'price']
    },
    {
      id: 3,
      difficulty: 'Easy',
      question: 'List all products ordered by price in descending order (highest first).',
      hint: 'Use ORDER BY with DESC to sort in descending order.',
      timeLimit: 180, // 3 minutes
      referenceSQL: 'SELECT * FROM products ORDER BY price DESC',
      solutionSQL: 'SELECT * FROM products ORDER BY price DESC',
      expectedColumns: ['id', 'name', 'category', 'price']
    },
    {
      id: 4,
      difficulty: 'Medium',
      question: 'Count how many orders exist for each product. Include the product name and show results sorted by order count descending.',
      hint: 'Use JOIN to combine tables, GROUP BY to aggregate, and COUNT to count rows.',
      timeLimit: 480, // 8 minutes
      referenceSQL: `
        SELECT p.id as product_id, p.name as product_name, COUNT(*) as order_count
        FROM products p
        JOIN orders o ON o.product_id = p.id
        GROUP BY p.id, p.name
        ORDER BY order_count DESC, p.id ASC
      `,
      solutionSQL: `
        SELECT p.id as product_id, p.name as product_name, COUNT(*) as order_count
        FROM products p
        JOIN orders o ON o.product_id = p.id
        GROUP BY p.id, p.name
        ORDER BY order_count DESC, p.id ASC
      `,
      expectedColumns: ['product_id', 'product_name', 'order_count']
    },
    {
      id: 5,
      difficulty: 'Medium',
      question: 'Show all orders with product names and calculate the total value (price × quantity) for each order. Sort by order ID.',
      hint: 'JOIN products and orders tables, then multiply price by quantity.',
      timeLimit: 480, // 8 minutes
      referenceSQL: `
        SELECT o.id as order_id, p.name as product_name, o.quantity,
               ROUND(p.price * o.quantity, 2) as total_value
        FROM orders o
        JOIN products p ON p.id = o.product_id
        ORDER BY o.id
      `,
      solutionSQL: `
        SELECT o.id as order_id, p.name as product_name, o.quantity,
               ROUND(p.price * o.quantity, 2) as total_value
        FROM orders o
        JOIN products p ON p.id = o.product_id
        ORDER BY o.id
      `,
      expectedColumns: ['order_id', 'product_name', 'quantity', 'total_value']
    }
  ]
};

export default beginnerTest;
